import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  CREATE_EMPLOYEE_VACATION,
  UPDATE_EMPLOYEE_VACATION,
} from '../../../graphql/team-availability-mutations';

const VACATION_TYPES = [
  { value: 'PTO', label: 'PTO' },
  { value: 'SICK', label: 'Sick' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'OTHER', label: 'Other' },
];

const buildInitialFormState = () => ({
  startDate: '',
  endDate: '',
  vacationType: '',
  reason: '',
});

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

function AdminUserTimeOffFormModal({ isOpen, onClose, userId, vacation, onSaved }) {
  const [formData, setFormData] = useState(buildInitialFormState);

  useEffect(() => {
    if (!isOpen) return;
    if (vacation) {
      setFormData({
        startDate: toDateInputValue(vacation.startDate),
        endDate: toDateInputValue(vacation.endDate),
        vacationType: vacation.vacationType || '',
        reason: vacation.reason || '',
      });
      return;
    }
    setFormData(buildInitialFormState());
  }, [isOpen, vacation]);

  const [createEmployeeVacation, { loading: creating }] = useMutation(CREATE_EMPLOYEE_VACATION, {
    onCompleted: () => {
      toast.success('Time off added');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add time off');
    },
  });

  const [updateEmployeeVacation, { loading: updating }] = useMutation(UPDATE_EMPLOYEE_VACATION, {
    onCompleted: () => {
      toast.success('Time off updated');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update time off');
    },
  });

  const isSaving = creating || updating;

  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formData.startDate || !formData.endDate) {
      toast.error('Start date and end date are required');
      return;
    }
    if (!formData.vacationType) {
      toast.error('Please select a time-off type');
      return;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error('End date must be on or after the start date');
      return;
    }
    const input = {
      userId,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      vacationType: formData.vacationType,
      reason: formData.reason?.trim() || null,
    };
    if (vacation) {
      updateEmployeeVacation({ variables: { id: vacation.id, input } });
      return;
    }
    createEmployeeVacation({ variables: { input } });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {vacation ? 'Edit Time Off' : 'Add Time Off'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="admin-time-off-start" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  id="admin-time-off-start"
                  type="date"
                  value={formData.startDate}
                  onChange={handleFieldChange('startDate')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="admin-time-off-end" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date *
                </label>
                <input
                  id="admin-time-off-end"
                  type="date"
                  value={formData.endDate}
                  onChange={handleFieldChange('endDate')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="admin-time-off-type" className="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                id="admin-time-off-type"
                value={formData.vacationType}
                onChange={handleFieldChange('vacationType')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                required
              >
                <option value="">Select type</option>
                {VACATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="admin-time-off-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason
              </label>
              <textarea
                id="admin-time-off-reason"
                value={formData.reason}
                onChange={handleFieldChange('reason')}
                rows={3}
                placeholder="Optional note"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : vacation ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminUserTimeOffFormModal;
